import { useReducer, useEffect, useState } from "react";
import type { U2000TableColumn } from "../../components/tables/basic/U2000BasicTableList";
import u2000SearchState, {
  U2000SearchStateEnumAction,
  type Criteria,
  type U2000SearchParameter,
  type U2000SearchParameterCriteria,
  type U2000SearchParameterField,
  type U2000SearchParameterSortedField,
  type U2000SearchState,
  type U2000SearchStateFilter,
  type U2000SearchStateSavedCriteria,
} from "./U2000SearchState";
import useU2000LocalStorage from "../../hooks/U2000UseLocalStorage";
import type { U2000Condition } from "../../components/tables/basic/U2000BasicSearch";
import type { U2000FieldItem } from "../../components/tables/commons/search/U2000BasicSearchField";
import type { SortedFieldItem } from "../../components/tables/commons/search/U2000BasicSearchSort";
import { getCriteriaApiFieldName } from "../../components/tables/commons/search/U2000CriteriaLookupRegistry";
import { generateTemporaryId } from "../../tools/U2000IdUtils";
import { u2000SnapshotCurrentFilterForStorage } from "../../tools/u2000SearchFilterStorage";
import { u2000CoerceLookupCriteriaCondition } from "../../tools/u2000SearchCriteriaFieldConditions";
import { u2000ComputeHeaderSortFields } from "../../tools/u2000ApplyHeaderSort";
import { u2000TodayOrgYmd } from "../../tools/U2000DateTimeHelpers";
import {
  u2000HydrateStoredFilterDates,
  u2000ResolveCriteriaDynamicDates,
  u2000ApplyDynamicDateSaveToCriterias,
} from "../../tools/u2000DynamicSearchDateUtils";

export interface U2000SaveFilterOptions {
  makeDatesDynamic?: boolean;
}

// Interface regroupant les handlers pour le form state
export interface U2000SearchStateHandlers {
  setColumns: (columns: U2000TableColumn[]) => void;
  getColumns: () => U2000TableColumn[];

  getCriterias: () => Criteria[];
  setCriterias: (criterias: Criteria[]) => void;
  addOrUpdateCriterias: (field: U2000TableColumn, condition: any, value: string | number | boolean, displayValue?: string) => void;
  deleteCriterias: (name: string) => void;

  setFields: (fields: U2000FieldItem[]) => void;
  getFields: () => U2000FieldItem[];
  addOrUpdateFields: (fields: U2000FieldItem[]) => void;
  moveFieldItem: (fieldName: string, direction: "up" | "down" | "top" | "bottom") => void;

  setSortedFields: (fields: SortedFieldItem[]) => void;
  getSortedFields: () => SortedFieldItem[];
  addOrUpdateSortedFields: (fields: SortedFieldItem[]) => void;
  moveSortedFieldItem: (fieldName: string, direction: "up" | "down" | "top" | "bottom") => void;
  changeSortedFieldSortOrder: (fieldName: string) => void;
  /** BL-0405 — clic en-tête : replace/toggle mono-colonne. `true` si le tri a changé. */
  applyHeaderSort: (column: U2000TableColumn) => boolean;

  getSavedFilters: () => U2000SearchStateSavedCriteria[];
  setCurrentFilter: (filter: U2000SearchStateFilter) => void;
  getCurrentFilter: () => U2000SearchStateFilter;
  restoreCurrentFilter: () => void;
  saveFilter: (filterName: string, isDefaultFilter?: boolean, options?: U2000SaveFilterOptions) => void;
  updateExistingFilter: (
    filterId: string,
    filterName: string,
    isDefaultFilter?: boolean,
    isFromFilterManagement?: boolean,
    options?: U2000SaveFilterOptions,
  ) => void;
  deleteFilter: (filterId: string) => void;
  selectFilterId: (id: string | null) => void;
  getSelectedFilterId: () => string | number | null;

  extractSearchParameter: () => U2000SearchParameter;

  keepFilterAfterSearch: (searchParameter: U2000SearchParameter) => void;
  hasAlreadyLoadedFilters: boolean;
  getLaunchSearch: () => boolean;
  getHideSearch: () => boolean;
}

export interface U2000SearchStateHandlersParams {
  luName?: string;
  launchSearch?: boolean;
  hideSearch?: boolean;
  searchState?: U2000SearchState;
}

export interface U2000SearchStateHandlersReturn {
  setColumns: (columns: U2000TableColumn[]) => void;
  getColumns: () => U2000TableColumn[];

  getCriterias: () => Criteria[];
  setCriterias: (criterias: Criteria[]) => void;
  addOrUpdateCriterias: (field: U2000TableColumn, condition: any, value: string | number | boolean, displayValue?: string) => void;
  deleteCriterias: (name: string) => void;

  getFields: () => U2000FieldItem[];
  setFields: (fields: U2000FieldItem[]) => void;
  addOrUpdateFields: (fields: U2000FieldItem[]) => void;
  moveFieldItem: (fieldName: string, direction: "up" | "down" | "top" | "bottom") => void;

  getSortedFields: () => SortedFieldItem[];
  setSortedFields: (fields: SortedFieldItem[]) => void;
  addOrUpdateSortedFields: (fields: SortedFieldItem[]) => void;
  moveSortedFieldItem: (fieldName: string, direction: "up" | "down" | "top" | "bottom") => void;
  changeSortedFieldSortOrder: (fieldName: string) => void;
  applyHeaderSort: (column: U2000TableColumn) => boolean;

  getSavedFilters: () => U2000SearchStateSavedCriteria[];
  saveFilter: (filterName: string, isDefaultFilter?: boolean) => void;
  setCurrentFilter: (filter: U2000SearchStateFilter) => void;
  restoreCurrentFilter: () => void;
  getCurrentFilter: () => U2000SearchStateFilter;
  updateExistingFilter: (filterId: string, filterName: string, isDefaultFilter?: boolean, isFromFilterManagement?: boolean) => void;
  deleteFilter: (filterId: string) => void;
  selectFilterId: (id: string | null) => void;
  getSelectedFilterId: () => string | null;

  extractSearchParameter: () => U2000SearchParameter;

  keepFilterAfterSearch: (searchParameter: U2000SearchParameter) => void;
  hasAlreadyLoadedFilters: boolean;
  getLaunchSearch: () => boolean;
  getHideSearch: () => boolean;
}

export const u2000IntSearchState: U2000SearchState = {
  columns: [],
  currentFilter: {
    fields: [],
    criterias: [],
    sortedFields: [],
  } as U2000SearchStateFilter,
  savedFilters: undefined as unknown as U2000SearchStateSavedCriteria[],
};

export function useU2000SearchStateHandlers(params: U2000SearchStateHandlersParams = {}): U2000SearchStateHandlersReturn {
  const { luName, launchSearch, hideSearch } = params;
  const [searchState, searchStateDispatch] = useReducer<U2000SearchState, any>(u2000SearchState, u2000IntSearchState);
  const [localCriterias, setLocalCriterias] = useU2000LocalStorage<U2000SearchStateSavedCriteria[]>(`${luName}SavedGridFilters`, []);
  const [selectedFilterId, setSelectedFilterId] = useState<string | null>(null);
  const [hasAlreadyLoadedFilters, setHasAlreadyLoadedFilters] = useState<boolean>(false);

  useEffect(() => {
    searchStateDispatch({
      type: U2000SearchStateEnumAction.UPDATESAVEDFILTERS,
      data: { savedFilters: localCriterias ?? [] },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync localStorage → reducer
  }, [localCriterias]);

  const getLaunchSearch = () => {
    return launchSearch ?? true;
  };

  const getHideSearch = () => {
    return hideSearch ?? false;
  };

  const getColumns = () => {
    return searchState.columns;
  };

  const setColumns = (columns: U2000TableColumn[]) => {
    searchStateDispatch({ type: U2000SearchStateEnumAction.SETCOLUMNS, data: { columns } });
  };

  const setCriterias = (criterias: Criteria[]) => {
    searchStateDispatch({ type: U2000SearchStateEnumAction.SETCRITERIAS, data: { criterias } });
  };

  const getCriterias = () => {
    return searchState.currentFilter.criterias;
  };

  const addOrUpdateCriterias = (field: U2000TableColumn, condition: U2000Condition, value: string | number | boolean, displayValue?: string) => {
    searchStateDispatch({
      type: U2000SearchStateEnumAction.ADDORUPDATECRITERIAS,
      data: {
        field,
        condition,
        value,
        displayValue,
      },
    });
  };

  const deleteCriterias = (name: string) => {
    searchStateDispatch({
      type: U2000SearchStateEnumAction.DELETECRITERIAS,
      data: {
        name,
      },
    });
  };

  const setFields = (fields: U2000FieldItem[]) => {
    searchStateDispatch({
      type: U2000SearchStateEnumAction.SETFIELDS,
      data: {
        fields,
      },
    });
  };

  const getFields = (): U2000FieldItem[] => {
    const fields = searchState.currentFilter.fields || [];
    // Normalize stored entries to FieldItem: if an entry already has `.field` keep it, otherwise wrap it.
    return (fields as any[]).map((f) => (f && (f as any).field ? (f as U2000FieldItem) : { field: f })) as U2000FieldItem[];
  };

  const addOrUpdateFields = (fields: U2000FieldItem[]) => {
    searchStateDispatch({
      type: U2000SearchStateEnumAction.SETFIELDS,
      data: {
        fields,
      },
    });
  };

  const moveFieldItem = (fieldName: string, direction: "up" | "down" | "top" | "bottom") => {
    const fields = getFields();
    const index = fields.findIndex((f) => f.field.name === fieldName);
    if (index === -1) return; // Not found

    let newIndex = index;
    if (direction === "up" && index > 0) newIndex = index - 1;
    else if (direction === "down" && index < fields.length - 1) newIndex = index + 1;
    else if (direction === "top") newIndex = 0;
    else if (direction === "bottom") newIndex = fields.length - 1;

    if (newIndex !== index) {
      const [movedItem] = fields.splice(index, 1);
      fields.splice(newIndex, 0, movedItem);
      addOrUpdateFields(fields);
    }
  };

  const setSortedFields = (fields: SortedFieldItem[]) => {
    searchStateDispatch({
      type: U2000SearchStateEnumAction.SETSORTEDFIELDS,
      data: {
        sortedFields: fields,
      },
    });
  };

  const getSortedFields = (): SortedFieldItem[] => {
    const sortedFields = searchState.currentFilter.sortedFields || [];
    // Normalize stored entries to SortedFieldItem: if an entry already has `.field` keep it, otherwise wrap it.
    return (sortedFields as any[]).map((f) => (f && (f as any).field ? (f as SortedFieldItem) : { field: f })) as SortedFieldItem[];
  };

  const addOrUpdateSortedFields = (sortedFields: SortedFieldItem[]) => {
    searchStateDispatch({
      type: U2000SearchStateEnumAction.SETSORTEDFIELDS,
      data: {
        sortedFields,
      },
    });
  };

  const moveSortedFieldItem = (fieldName: string, direction: "up" | "down" | "top" | "bottom") => {
    const fields = getSortedFields();
    const index = fields.findIndex((f) => f.field.name === fieldName);
    if (index === -1) return; // Not found

    let newIndex = index;
    if (direction === "up" && index > 0) newIndex = index - 1;
    else if (direction === "down" && index < fields.length - 1) newIndex = index + 1;
    else if (direction === "top") newIndex = 0;
    else if (direction === "bottom") newIndex = fields.length - 1;

    if (newIndex !== index) {
      const [movedItem] = fields.splice(index, 1);
      fields.splice(newIndex, 0, movedItem);
      addOrUpdateSortedFields(fields);
    }
  };

  const changeSortedFieldSortOrder = (fieldName: string) => {
    const fields = getSortedFields();
    const updated = fields.map((f) => {
      if (!f || !f.field) return f;
      return f.field.name === fieldName ? { ...f, sortOrder: f.sortOrder === "ASC" ? "DESC" : "ASC" } : f;
    });
    addOrUpdateSortedFields(updated as SortedFieldItem[]);
  };

  /** BL-0405 — replace / toggle mono-colonne depuis un clic en-tête. */
  const applyHeaderSort = (column: U2000TableColumn): boolean => {
    const next = u2000ComputeHeaderSortFields(column, getSortedFields());
    if (!next) return false;
    setSortedFields(next);
    return true;
  };

  const getSavedFilters = (): U2000SearchStateSavedCriteria[] => {
    return searchState.savedFilters ?? [];
  };

  const snapshotCurrentFilter = (options?: U2000SaveFilterOptions): U2000SearchStateFilter => {
    const referenceTodayYmd = u2000TodayOrgYmd();
    return u2000SnapshotCurrentFilterForStorage(getCriterias, getFields, getSortedFields, {
      makeDatesDynamic: options?.makeDatesDynamic,
      referenceTodayYmd,
    });
  };

  const hydrateStoredFilter = (filter: U2000SearchStateFilter): U2000SearchStateFilter =>
    u2000HydrateStoredFilterDates(filter, u2000TodayOrgYmd());

  const getCurrentFilter = (): U2000SearchStateFilter => {
    return searchState.currentFilter;
  };

  const setCurrentFilter = (filter: U2000SearchStateFilter) => {
    searchStateDispatch({
      type: U2000SearchStateEnumAction.SETCURRENTFILTER,
      data: {
        filter,
      },
    });
  };

  const restoreCurrentFilter = () => {
    searchStateDispatch({
      type: U2000SearchStateEnumAction.RESTOREFILTER,
    });
  };

  const updateSaveFilter = (newSavedFilters: U2000SearchStateSavedCriteria[]) => {
    setLocalCriterias(newSavedFilters);
    searchStateDispatch({
      type: U2000SearchStateEnumAction.UPDATESAVEDFILTERS,
      data: {
        savedFilters: newSavedFilters,
      },
    });
  };

  const syncCriteriasAfterSave = (options?: U2000SaveFilterOptions) => {
    const todayYmd = u2000TodayOrgYmd();
    const criterias = u2000ApplyDynamicDateSaveToCriterias(
      getCriterias(),
      Boolean(options?.makeDatesDynamic),
      todayYmd,
    );
    setCurrentFilter({
      ...getCurrentFilter(),
      criterias,
    });
  };

  const saveFilter = (filterName: string, isDefaultFilter: boolean = false, options?: U2000SaveFilterOptions) => {
    // Check if a filter with the same name exists
    let newSavedFilters: U2000SearchStateSavedCriteria[] = [];
    const savedFilters = getSavedFilters();
    const filterSnapshot = snapshotCurrentFilter(options);
    const existing = savedFilters.find((c) => c.name === filterName);
    if (existing) {
      // Update existing filter
      newSavedFilters = savedFilters.map((c) => {
        if (c.name === filterName) {
          return { ...c, isDefaultFilter, filter: filterSnapshot };
        } else if (isDefaultFilter) {
          // Unset default for all others if this one is set as default
          return { ...c, isDefaultFilter: false };
        } else {
          return c;
        }
      });
      updateSaveFilter(newSavedFilters);
      setSelectedFilterId(existing.id);
      syncCriteriasAfterSave(options);
    } else {
      // Create new filter with a unique UUID
      const id = generateTemporaryId();
      const saveCriterias: U2000SearchStateSavedCriteria = {
        id: id,
        name: filterName,
        isDefaultFilter: isDefaultFilter,
        filter: filterSnapshot,
      };
      if (isDefaultFilter) {
        // Unset default for all others
        newSavedFilters = savedFilters.map((c) => ({ ...c, isDefaultFilter: false }));
        newSavedFilters = [...newSavedFilters, saveCriterias];
      } else {
        newSavedFilters = [...savedFilters, saveCriterias];
      }
      updateSaveFilter(newSavedFilters);
      setSelectedFilterId(id);
      syncCriteriasAfterSave(options);
    }
  };

  const updateExistingFilter = (
    filterId: string,
    filterName: string,
    isDefaultFilter?: boolean,
    isFromFilterManagement: boolean = false,
    options?: U2000SaveFilterOptions,
  ) => {
    let newSavedFilters: U2000SearchStateSavedCriteria[] = [];
    const existing = getSavedFilters().find((c) => c.id === filterId);
    if (existing) {
      // Update existing filter
      newSavedFilters = getSavedFilters().map((c) => {
        if (c.id === filterId) {
          if (!isFromFilterManagement) {
            return {
              ...c,
              name: filterName,
              isDefaultFilter: isDefaultFilter ?? c.isDefaultFilter,
              filter: snapshotCurrentFilter(options),
            };
          } else {
            return { ...c, name: filterName, isDefaultFilter: isDefaultFilter ?? c.isDefaultFilter };
          }
        } else if (isDefaultFilter) {
          // Unset default for all others if this one is set as default
          return { ...c, isDefaultFilter: false };
        } else {
          return c;
        }
      });
      updateSaveFilter(newSavedFilters);
      if (!isFromFilterManagement) {
        setSelectedFilterId(existing.id);
        syncCriteriasAfterSave(options);
      }
    }
  };

  const deleteFilter = (filterId: string) => {
    const newSavedFilters = getSavedFilters().filter((c) => c.id !== filterId);
    updateSaveFilter(newSavedFilters);
    // Reset selected filter if it was the deleted one
    if (selectedFilterId === filterId) {
      setSelectedFilterId(null);
      // Reset current filter to empty
      setCurrentFilter({
        fields: [],
        criterias: [],
        sortedFields: [],
      });
    }
  };

  const selectFilterId = (id: string | null) => {
    setSelectedFilterId(id);
    if (id) {
      const selected = getSavedFilters().find((c) => c.id === id);
      if (selected && selected.filter) {
        setCurrentFilter(hydrateStoredFilter(selected.filter));
      }
    }
  };

  const getSelectedFilterId = () => {
    return selectedFilterId;
  };

  const extractSearchParameter = (): U2000SearchParameter => {
    const todayYmd = u2000TodayOrgYmd();
    const CriteriaParameters: U2000SearchParameterCriteria[] = getCriterias().map((c) => {
      const resolved = u2000ResolveCriteriaDynamicDates(c, todayYmd);
      const condition = u2000CoerceLookupCriteriaCondition(resolved.field, resolved.condition);
      return {
        field: getCriteriaApiFieldName(resolved.field),
        operator: condition.name,
        value: resolved.value,
        type: resolved.field.type,
      };
    });

    const FieldParameters: U2000SearchParameterField[] = getFields().map((f) => ({
      name: f.field.name,
    }));

    const SortedFieldParameters: U2000SearchParameterSortedField[] = getSortedFields().map((s) => ({
      name: s.field.name,
      sortOrder: (s.sortOrder as "ASC" | "DESC") || "ASC",
    }));

    const searchParameters: U2000SearchParameter = { criterias: CriteriaParameters, fields: FieldParameters, sortedFields: SortedFieldParameters };
    return searchParameters;
  };

  const keepFilterAfterSearch = (_searchParameter: U2000SearchParameter) => {
    setHasAlreadyLoadedFilters(true);
  };

  return {
    setColumns,
    getColumns,
    getCriterias,
    setCriterias,
    addOrUpdateCriterias,
    deleteCriterias,
    getSavedFilters,
    setFields,
    getFields,
    addOrUpdateFields,
    moveFieldItem,
    getSortedFields,
    setSortedFields,
    addOrUpdateSortedFields,
    moveSortedFieldItem,
    changeSortedFieldSortOrder,
    applyHeaderSort,
    saveFilter,
    restoreCurrentFilter,
    setCurrentFilter,
    getCurrentFilter,
    updateExistingFilter,
    deleteFilter,
    selectFilterId,
    getSelectedFilterId,
    extractSearchParameter,
    keepFilterAfterSearch,
    hasAlreadyLoadedFilters,
    getLaunchSearch,
    getHideSearch,
  };
}
